import type { AppLoadContext, LoaderFunctionArgs } from "react-router";

import { topasAggregatesClient } from "~/features/integration/topas-aggregates/client.server";
import { fetchJson } from "~/utils/fetch-json";
import { fetchFromKotlinBFFIfAllowed } from "~/utils/kotlin-bff.server";
import { notFound } from "~/utils/response.server";

import { useQuery } from "@tanstack/react-query";
import { toMilliseconds } from "duration-fns";
import type { Jsonify } from "type-fest";

export type VehicleSetIdsData = Jsonify<
  Awaited<ReturnType<ReturnType<typeof fetchFromTopasAggregates>>>
>;

const fetchFromTopasAggregates = (context: AppLoadContext) => async () => {
  const client = topasAggregatesClient(context);
  const response = await client.getAllVehicleSets({
    countryCode: "NO",
    validAt: new Date(),
  });

  if (!response.length) {
    throw notFound("No vehicle sets found");
  }

  return response.sort((a, b) => a.setId.localeCompare(b.setId));
};

export const loader = async ({ context }: LoaderFunctionArgs) => {
  if (context.session?.user.rbac.isDeveloper) {
    return await fetchFromKotlinBFFIfAllowed({
      context,
      path: "/v0/rollingstock/vehicle-sets",
      fallbackFetcher: fetchFromTopasAggregates(context),
    });
  }

  return await fetchFromTopasAggregates(context)();
};

export const VEHICLE_SETS_QUERY_KEY = ["vehicle-sets"];

export const vehicleSetsQueryFn = () =>
  fetchJson<VehicleSetIdsData>("/api/vehicle-sets");

export const useVehicleSets = () => {
  return useQuery({
    queryKey: VEHICLE_SETS_QUERY_KEY,
    queryFn: vehicleSetsQueryFn,
    gcTime: Infinity,
    staleTime: toMilliseconds({
      days: 1,
    }),
  });
};
